import { getItemCount, getTotal } from "./cartStore";

const formatPrice = (value) =>
  `$${value.toLocaleString("es-AR", { minimumFractionDigits: 0 })}`;

// Función para formatear la presentación del producto
// Formato: "{cantidad} {presentacion}" (ej: "4 porcion", "1 porcion", "2 unidades")
// IMPORTANTE: usa productQuantity (cantidad del producto) no quantity (cantidad en carrito)
const formatProductPresentation = (item) => {
  const productQuantity = item.productQuantity || 1;
  const productPresentation = item.productPresentation || item.presentation || "porcion";
  return `${productQuantity} ${productPresentation}`;
};

export const createCartUI = ({
  rootSelector,
  toggleSelector,
  countSelector,
  onRemove,
  onSetQuantity,
  onCheckout,
}) => {
  const root = document.querySelector(rootSelector);
  const toggle = document.querySelector(toggleSelector);
  const count = document.querySelector(countSelector);
  let isOpen = false;
  let previousFocusedElement = null;

  if (!root) return null;

  const render = (cart) => {
    const total = getTotal(cart);
    const itemCount = getItemCount(cart);
    const cartLabel = itemCount === 1 ? "Ver carrito, 1 producto" : `Ver carrito, ${itemCount} productos`;

    if (count) {
      count.textContent = `(${itemCount})`;
      count.setAttribute("aria-label", itemCount === 1 ? "1 producto" : `${itemCount} productos`);
    }

    if (toggle) {
      toggle.setAttribute("aria-label", cartLabel);
      toggle.setAttribute("aria-expanded", String(isOpen));
    }

    const itemsHtml = cart.items.length
      ? cart.items
          .map(
            (item) => {
              const productPresentation = formatProductPresentation(item);
              const itemPrice = formatPrice(item.price);
              const itemSubtotal = formatPrice(item.price * item.quantity);
              const imageUrl = item.imageUrl || item.imagen || null;
              
              return `
          <li class="cart-item">
            <div class="cart-item__content">
              ${imageUrl 
                ? `<div class="cart-item__image">
                    <img src="${imageUrl}" alt="${item.name}" loading="lazy" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
                    <div class="cart-item__image-placeholder" style="display: none;">
                      <span>🍕</span>
                    </div>
                  </div>`
                : `<div class="cart-item__image-placeholder">
                    <span>🍕</span>
                  </div>`
              }
              <div class="cart-item__info">
                <strong class="cart-item__name">${item.name}</strong>
                <span class="cart-item__presentation">${productPresentation}</span>
                <div class="cart-item__price-info">
                  <span class="cart-item__unit-price">${itemPrice} c/u</span>
                  <span class="cart-item__subtotal">${itemSubtotal}</span>
                </div>
              </div>
            </div>
            <div class="cart-item__actions">
              <div class="cart-item__quantity-controls">
                <button type="button" class="btn-quantity" data-action="decrease" data-id="${
                  item.id
                }" data-presentation="${item.presentation}" aria-label="Disminuir cantidad de ${item.name}">−</button>
                <span class="cart-item__quantity" aria-label="Cantidad: ${item.quantity}">${item.quantity}</span>
                <button type="button" class="btn-quantity" data-action="increase" data-id="${
                  item.id
                }" data-presentation="${item.presentation}" aria-label="Aumentar cantidad de ${item.name}">+</button>
              </div>
              <button type="button" class="btn-remove" data-action="remove" data-id="${
                item.id
              }" data-presentation="${item.presentation}" aria-label="Quitar ${item.name} del carrito">
                Quitar
              </button>
            </div>
          </li>
        `;
            }
          )
          .join("")
      : `
        <li class="cart-empty">
          Tu carrito esta vacio. Agrega productos del catalogo.
        </li>
      `;

    root.innerHTML = `
      <div class="cart-drawer ${isOpen ? "is-open" : ""}">
        <div class="cart-drawer__overlay" data-action="close" aria-hidden="true"></div>
        <aside id="cart-drawer-panel" class="cart-drawer__panel" role="dialog" aria-modal="true" aria-label="Carrito de compras" tabindex="-1">
          <div class="cart-drawer__header">
            <h3>Tu carrito</h3>
            <button type="button" class="btn btn--ghost" data-action="close" aria-label="Cerrar carrito">Cerrar</button>
          </div>
          <ul class="cart-list">
            ${itemsHtml}
          </ul>
          <div class="cart-drawer__footer">
            <div class="cart-total">
              <span>Total estimado</span>
              <strong>${formatPrice(total)}</strong>
            </div>
            <button class="btn btn--primary" type="button" data-action="checkout">
              Finalizar pedido
            </button>
          </div>
        </aside>
      </div>
    `;
  };

  const open = () => {
    previousFocusedElement = document.activeElement;
    isOpen = true;
    root.querySelector(".cart-drawer")?.classList.add("is-open");
    toggle?.setAttribute("aria-expanded", "true");
    root.querySelector(".cart-drawer__panel [data-action='close']")?.focus();
  };

  const close = () => {
    isOpen = false;
    root.querySelector(".cart-drawer")?.classList.remove("is-open");
    toggle?.setAttribute("aria-expanded", "false");
    if (previousFocusedElement && typeof previousFocusedElement.focus === "function") {
      previousFocusedElement.focus();
      previousFocusedElement = null;
    }
  };

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && isOpen) {
      event.preventDefault();
      close();
    }
  });

  toggle?.addEventListener("click", () => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  });

  root.addEventListener("click", (event) => {
    const actionEl = event.target?.closest("[data-action]");
    if (!actionEl || !root.contains(actionEl)) return;

    const { action, id, presentation } = actionEl.dataset;

    if (action === "close") {
      close();
    }

    if (action === "remove") {
      onRemove?.(id, presentation);
    }

    if (action === "increase") {
      onSetQuantity?.(id, presentation, "increase");
    }

    if (action === "decrease") {
      onSetQuantity?.(id, presentation, "decrease");
    }

    if (action === "checkout") {
      onCheckout?.();
    }
  });

  return { render, open, close };
};
